import { useState } from "react";
import PropTypes from "prop-types";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { AlertNote } from "../../components/AlertNote";
import { Button } from "../../components/Button";
import { CustomSelector } from "../../components/CustomSelector";
import { Label } from "../../components/Label";
import {
  MetricsDateRangeBar,
  RANGE_PRESETS,
  defaultDateSelection,
  toIsoDate,
} from "../../components/MetricsDateRangeBar";
import { UnifiedModal } from "../../components/UnifiedModal";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import { useTaskPolling } from "../../hooks/useTaskPolling";
import { generateReport } from "../../services/reportsService";

const REPORT_PRESETS = RANGE_PRESETS.filter((preset) =>
  ["last_7", "last_30", "last_3m"].includes(preset.value),
);

// Generation runs several AI calls; 3 s × 100 polls ≈ 5 minutes before giving up.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 100;

/**
 * Picks a client and a date range, starts the generation task and follows it
 * until the report exists, then opens it.
 */
export function GenerateReportModal({
  onClose,
  basePath,
  defaultClientId,
  agencyId,
}) {
  const navigate = useNavigate();
  const { interceptorReady } = useAuth();
  const [clientId, setClientId] = useState(defaultClientId);
  const [dateSelection, setDateSelection] = useState(() => ({
    ...defaultDateSelection(REPORT_PRESETS, "last_30"),
    compareEnabled: false,
  }));
  const [submitting, setSubmitting] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [error, setError] = useState(null);
  const [existingReportId, setExistingReportId] = useState(null);

  const { data: clients, isLoading: isLoadingClients } = getData(
    `clients/options/?ordering=name${agencyId ? `&agency=${agencyId}` : ""}`,
    interceptorReady,
    0,
  );
  const clientOptions = (clients || []).map((client) => ({
    value: client.id,
    label: client.name,
  }));

  const { isPolling } = useTaskPolling(taskId, {
    interval: POLL_INTERVAL_MS,
    maxAttempts: POLL_MAX_ATTEMPTS,
    onSuccess: (result) => navigate(`${basePath}/${result.report_id}`),
    onError: (message) => {
      setTaskId(null);
      setError(
        message.startsWith("Task timeout")
          ? "La generación está tardando más de lo previsto. Revisa el listado en unos minutos."
          : "No se pudo generar el informe. Inténtalo de nuevo.",
      );
    },
  });

  const handleGenerate = async () => {
    setSubmitting(true);
    setError(null);
    setExistingReportId(null);
    try {
      const data = await generateReport(
        clientId,
        toIsoDate(dateSelection.range.from),
        toIsoDate(dateSelection.range.to)
      );
      setTaskId(data.task_id);
    } catch (err) {
      const body = err.response?.data;
      if (body?.code === "report_exists") setExistingReportId(body.report_id);
      setError(body?.detail || "No se pudo iniciar la generación del informe.");
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || isPolling;

  return (
    <UnifiedModal open onClose={onClose} title="Generar informe" maxWidth="sm">
      <Stack spacing={2.5}>
        <Stack spacing={1}>
          <Label>Cliente</Label>
          <CustomSelector
            searchable
            value={clientId}
            onChange={setClientId}
            options={clientOptions}
            placeholder={
              isLoadingClients
                ? "Cargando clientes..."
                : "Selecciona un cliente"
            }
            disabled={busy || isLoadingClients}
          />
        </Stack>

        <Stack spacing={1}>
          <Label>Periodo</Label>
          <MetricsDateRangeBar
            value={dateSelection}
            onChange={setDateSelection}
            presets={REPORT_PRESETS}
            presetList
            customRangeHint="Máximo 3 meses"
            overlayZIndex={100001}
            disabled={busy}
          />
          <Typography variant="caption" color="text.secondary">
            Se compara con el periodo anterior de la misma duración.
          </Typography>
        </Stack>

        {isPolling && (
          <Stack spacing={1}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary">
              Generando el informe. Puede tardar unos minutos; al terminar se
              abrirá automáticamente.
            </Typography>
          </Stack>
        )}

        {error && (
          <AlertNote
            variant={existingReportId ? "warning" : "danger"}
            action={
              existingReportId && (
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => navigate(`${basePath}/${existingReportId}`)}
                >
                  Abrir informe
                </Button>
              )
            }
          >
            {error}
          </AlertNote>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button variant="outline" onClick={onClose}>
            {isPolling ? "Cerrar" : "Cancelar"}
          </Button>
          <Button onClick={handleGenerate} disabled={busy || !clientId}>
            {busy ? "Generando..." : "Generar informe"}
          </Button>
        </Box>
      </Stack>
    </UnifiedModal>
  );
}

GenerateReportModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  basePath: PropTypes.string.isRequired,
  defaultClientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  agencyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
